import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SpeedIcon from '@mui/icons-material/Speed';
import QuizIcon from '@mui/icons-material/Quiz';
import LanIcon from '@mui/icons-material/Lan';
import CodeIcon from '@mui/icons-material/Code';
import ExtensionIcon from '@mui/icons-material/Extension';
import SyncIcon from '@mui/icons-material/Sync';
import LayersIcon from '@mui/icons-material/Layers';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BuildIcon from '@mui/icons-material/Build';
import SchemaIcon from '@mui/icons-material/Schema';
import StorageIcon from '@mui/icons-material/Storage';
import NextPlanIcon from '@mui/icons-material/NextPlan';
import BrushIcon from '@mui/icons-material/Brush';

export const foundation = {
  title: "Zero to Senior",
  subtitle: "From First Line of Code to Senior Engineer",
  badge: "NEW",
  icon: <RocketLaunchIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
  description: "The most comprehensive frontend roadmap on the internet. Go from absolute beginner to a production-ready Senior Frontend Engineer in 17 structured phases.",
  features: [
    "Phase-by-Phase Comprehensive Roadmap",
    "Progressive Unlocking & Gamification",
    "Interactive UI Scenarios"
  ],
  price: 999,
  mrp: 999,
  topics: [
    {
      id: "phase1",
      title: "Phase 1: Web Foundations",
      desc: "Internet, DNS, Semantic HTML, CSS, and styling basics.",
      badge: "Phase 1",
      group: "Phase 1",
      icon: <DashboardIcon />,
      problems: [
        { id: "z2h_p1_1", title: "Internet Fundamentals", badge: "Core", fileUrl: "/documents/frontend_foundations/internet_fundamentals.md" },
        { id: "z2h_p1_2", title: "Semantic HTML5 & The DOM", badge: "HTML", fileUrl: "/documents/frontend_foundations/html_dom.md" },
        { id: "z2h_p1_3", title: "CSS Essentials", badge: "CSS", fileUrl: "/documents/frontend_foundations/css_essentials.md" }
      ]
    },
    {
      id: "phase2",
      title: "Phase 2: Advanced CSS",
      desc: "Flexbox, Grid Layouts, Responsive Design, and CSS Animations.",
      badge: "Phase 2",
      group: "Phase 2",
      icon: <DashboardIcon />,
      problems: [
        { id: "z2h_p2_1", title: "CSS Layouts: Flexbox & Grid", badge: "Layout", fileUrl: "/documents/frontend_foundations/css_layouts_flexgrid.md" },
        { id: "z2h_p2_2", title: "Responsive Web Design", badge: "Mobile", fileUrl: "/documents/frontend_foundations/css_responsive.md" },
        { id: "z2h_p2_3", title: "The Box Model", badge: "Core", fileUrl: "/documents/interview/prep_css_box_model.md" },
        { id: "z2h_p2_4", title: "CSS Animations & Transitions", badge: "Motion", fileUrl: "/documents/frontend_foundations/css_animations.md" }
      ]
    },
    {
      id: "phase3",
      title: "Phase 3: Modern CSS Architectures",
      desc: "Tailwind CSS, Utility-first logic, and Atomic design.",
      badge: "Phase 3",
      group: "Phase 3",
      icon: <BrushIcon />,
      problems: [
        { id: "z2h_p3_1", title: "Tailwind CSS & Atomic Design", badge: "Tailwind", fileUrl: "/documents/frontend_foundations/tailwind_css.md" },
        { id: "z2h_p3_2", title: "CSS-in-JS & Styled Components", badge: "CSS-in-JS", fileUrl: "/documents/frontend_foundations/css_in_js_styled.md" }
      ]
    },
    {
      id: "phase4",
      title: "Phase 4: Dev Tools & Version Control",
      desc: "Mastering Git, GitHub, and Package Managers.",
      badge: "Phase 4",
      group: "Phase 4",
      icon: <BuildIcon />,
      problems: [
        { id: "z2h_p4_1", title: "Version Control: Git Fundamentals", badge: "Git", fileUrl: "/documents/frontend_foundations/version_control_git.md" },
        { id: "z2h_p4_2", title: "Package Managers: NPM & Yarn", badge: "NPM", fileUrl: "/documents/frontend_foundations/package_managers_npm.md" }
      ]
    },
    {
      id: "phase5",
      title: "Phase 5: JavaScript Core Mechanics",
      desc: "Execution context, scoping, closures, and the memory model.",
      badge: "Phase 5",
      group: "Phase 5",
      icon: <CodeIcon />,
      problems: [
        { id: "z2h_p5_1", title: "Global Execution Context", badge: "Core", fileUrl: "/documents/javascript/js_gec.md" },
        { id: "z2h_p5_2", title: "Hoisting & Temporal Dead Zone", badge: "Scope", fileUrl: "/documents/javascript/js_hoisting.md" },
        { id: "z2h_p5_3", title: "Mastering Closures", badge: "Memory", fileUrl: "/documents/javascript/js_closures.md" },
        { id: "z2h_p5_4", title: "The 'this' Keyword", badge: "Context", fileUrl: "/documents/javascript/js_this_binding.md" },
        { id: "z2h_p5_5", title: "Call Stack Deep Dive", badge: "Core", fileUrl: "/documents/javascript/js_call_stack.md" },
        { id: "z2h_p5_6", title: "Scope & Lexical Environment", badge: "Scope", fileUrl: "/documents/javascript/js_scope.md" }
      ]
    },
    {
      id: "phase6",
      title: "Phase 6: JavaScript Async & Patterns",
      desc: "The Event Loop, Promises, async/await, Generators, and functional techniques.",
      badge: "Phase 6",
      group: "Phase 6",
      icon: <SyncIcon />,
      problems: [
        { id: "z2h_p6_1", title: "The Event Loop Mechanism", badge: "Async", fileUrl: "/documents/javascript/js_event_loop.md" },
        { id: "z2h_p6_2", title: "Promises & Microtasks", badge: "Promises", fileUrl: "/documents/javascript/js_promises_mastery.md" },
        { id: "z2h_p6_3", title: "Array Iteration Mastery", badge: "Functional", fileUrl: "/documents/javascript/js_array_map.md" },
        { id: "z2h_p6_4", title: "Prototypal Inheritance", badge: "OOP", fileUrl: "/documents/javascript/js_prototypes.md" },
        { id: "z2h_p6_5", title: "async/await Deep Dive", badge: "Async", fileUrl: "/documents/javascript/js_async_await.md" },
        { id: "z2h_p6_6", title: "Generators & Iterators", badge: "Advanced", fileUrl: "/documents/javascript/js_generators.md" },
        { id: "z2h_p6_7", title: "Error Handling Patterns", badge: "Patterns", fileUrl: "/documents/javascript/js_error_handling.md" }
      ]
    },
    {
      id: "phase7",
      title: "Phase 7: Web APIs & DOM",
      desc: "DOM Manipulation, Events, Storage, Fetch API, and WebSockets.",
      badge: "Phase 7",
      group: "Phase 7",
      icon: <LanIcon />,
      problems: [
        { id: "z2h_p7_1", title: "JS Browser & DOM", badge: "DOM", fileUrl: "/documents/interview/prep_js_dom_browser.md" },
        { id: "z2h_p7_2", title: "Web Storage Strategies", badge: "API", fileUrl: "/documents/interview/prep_api_storage.md" },
        { id: "z2h_p7_3", title: "Modern Observers & Threads", badge: "Threads", fileUrl: "/documents/interview/prep_api_modern.md" },
        { id: "z2h_p7_4", title: "Fetch API & HTTP Deep Dive", badge: "HTTP", fileUrl: "/documents/javascript/js_fetch_http.md" },
        { id: "z2h_p7_5", title: "WebSockets & Real-time APIs", badge: "Real-time", fileUrl: "/documents/javascript/js_websockets.md" }
      ]
    },
    {
      id: "phase8",
      title: "Phase 8: React Fundamentals",
      desc: "Components, Props, JSX, and unidirectional data.",
      badge: "Phase 8",
      group: "Phase 8",
      icon: <LayersIcon />,
      problems: [
        { id: "z2h_p8_1", title: "JSX & Component Philosophy", badge: "JSX", fileUrl: "/documents/interview/prep_react_jsx.md" },
        { id: "z2h_p8_2", title: "Data Flow & Props", badge: "Props", fileUrl: "/documents/interview/prep_react_props.md" },
        { id: "z2h_p8_3", title: "State & Immutability", badge: "State", fileUrl: "/documents/interview/prep_react_state_basics.md" },
        { id: "z2h_p8_4", title: "VDOM & Reconciliation", badge: "Core", fileUrl: "/documents/react/react_vdom.md" }
      ]
    },
    {
      id: "phase9",
      title: "Phase 9: Hooks Mastery",
      desc: "useState, useEffect, useMemo, useCallback, and Custom Hooks.",
      badge: "Phase 9",
      group: "Phase 9",
      icon: <ExtensionIcon />,
      problems: [
        { id: "z2h_p9_1", title: "The useState Hook", badge: "State", fileUrl: "/documents/interview/prep_hook_useState.md" },
        { id: "z2h_p9_2", title: "The useEffect Hook Lifecycle", badge: "Effects", fileUrl: "/documents/interview/prep_hook_useEffect.md" },
        { id: "z2h_p9_3", title: "Refs & Imperative Code", badge: "Ref", fileUrl: "/documents/interview/prep_hook_refs.md" },
        { id: "z2h_p9_4", title: "Hooks Internals & Execution", badge: "Expert", fileUrl: "/documents/react/react_hooks_internals.md" },
        { id: "z2h_p9_5", title: "useMemo: Memoizing Values", badge: "Perf", fileUrl: "/documents/react/react_perf_usememo.md" },
        { id: "z2h_p9_6", title: "useCallback: Stable References", badge: "Perf", fileUrl: "/documents/react/react_perf_usecallback.md" },
        { id: "z2h_p9_7", title: "Custom Hooks: Logic Reuse", badge: "Patterns", fileUrl: "/documents/interview/prep_hook_custom.md" }
      ]
    },
    {
      id: "phase10",
      title: "Phase 10: Enterprise State & Fetching",
      desc: "React Query, Redux Toolkit, Zustand, and state architecture.",
      badge: "Phase 10",
      group: "Phase 10",
      icon: <StorageIcon />,
      problems: [
        { id: "z2h_p10_1", title: "React Query & Server State", badge: "Query", fileUrl: "/documents/react/react_query.md" },
        { id: "z2h_p10_2", title: "React Zustand Architecture", badge: "Zustand", fileUrl: "/documents/react/react_zustand.md" },
        { id: "z2h_p10_3", title: "Redux Toolkit Foundations", badge: "Redux", fileUrl: "/documents/react/react_redux.md" },
        { id: "z2h_p10_4", title: "State Management Comparison", badge: "Architecture", fileUrl: "/documents/react/react_state_alt.md" }
      ]
    },
    {
      id: "phase11",
      title: "Phase 11: React Performance & Arch",
      desc: "Memoization, Context API, Suspense, and Global State Patterns.",
      badge: "Phase 11",
      group: "Phase 11",
      icon: <SpeedIcon />,
      problems: [
        { id: "z2h_p11_1", title: "Render Cycle Optimization", badge: "Perf", fileUrl: "/documents/react/react_optimization.md" },
        { id: "z2h_p11_2", title: "Context API & Global State", badge: "Context", fileUrl: "/documents/interview/prep_arch_context.md" },
        { id: "z2h_p11_3", title: "React Router & Error Boundaries", badge: "Router", fileUrl: "/documents/interview/prep_arch_errors.md" },
        { id: "z2h_p11_4", title: "React 18 & Concurrency", badge: "Modern", fileUrl: "/documents/interview/prep_master_react18.md" },
        { id: "z2h_p11_5", title: "Suspense & Lazy Loading", badge: "Perf", fileUrl: "/documents/react/react_suspense_lazy.md" }
      ]
    },
    {
      id: "phase12",
      title: "Phase 12: Build Tools & Security",
      desc: "Bundlers, ESLint, Prettier, CI/CD, and Security constraints.",
      badge: "Phase 12",
      group: "Phase 12",
      icon: <BuildIcon />,
      problems: [
        { id: "z2h_p12_1", title: "Build Tools: Vite & Webpack", badge: "Tools", fileUrl: "/documents/frontend_foundations/build_tools_vite.md" },
        { id: "z2h_p12_2", title: "Web Security Basics", badge: "Security", fileUrl: "/documents/frontend_foundations/web_security_basics.md" },
        { id: "z2h_p12_3", title: "XSS, CSRF & Auth Tokens", badge: "Security", fileUrl: "/documents/interview/prep_eco_security.md" },
        { id: "z2h_p12_4", title: "ESLint & Prettier", badge: "Quality", fileUrl: "/documents/frontend_foundations/prep_eslint_prettier.md" },
        { id: "z2h_p12_5", title: "CI/CD with GitHub Actions", badge: "DevOps", fileUrl: "/documents/frontend_foundations/prep_cicd_pipelines.md" }
      ]
    },
    {
      id: "phase13",
      title: "Phase 13: Next.js & Server Rendering",
      desc: "SSR, SSG, Hydration, RSC, and the Next.js App Router.",
      badge: "Phase 13",
      group: "Phase 13",
      icon: <NextPlanIcon />,
      problems: [
        { id: "z2h_p13_1", title: "Mastering SSR & CSR", badge: "Theory", fileUrl: "/documents/interview/prep_master_ssr.md" },
        { id: "z2h_p13_2", title: "Next.js Basics", badge: "Next.js", fileUrl: "/documents/react/react_nextjs_basics.md" },
        { id: "z2h_p13_3", title: "The Next.js App Router", badge: "Router", fileUrl: "/documents/interview/prep_next_app_router.md" },
        { id: "z2h_p13_4", title: "Advanced Data Fetching (SSR/SSG)", badge: "Fetch", fileUrl: "/documents/interview/prep_next_data_fetching.md" },
        { id: "z2h_p13_5", title: "React Server Components", badge: "RSC", fileUrl: "/documents/react/react_server_components.md" }
      ]
    },
    {
      id: "phase14",
      title: "Phase 14: TypeScript Engineering",
      desc: "Interfaces, Generics, Utility Types, and static typings for React.",
      badge: "Phase 14",
      group: "Phase 14",
      icon: <CodeIcon />,
      problems: [
        { id: "z2h_p14_1", title: "Core Types & Interfaces", badge: "TS", fileUrl: "/documents/interview/prep_ts_types.md" },
        { id: "z2h_p14_2", title: "Advanced Generics", badge: "TS", fileUrl: "/documents/interview/prep_ts_advanced.md" },
        { id: "z2h_p14_3", title: "TypeScript with React", badge: "React", fileUrl: "/documents/interview/prep_ts_react.md" },
        { id: "z2h_p14_4", title: "TypeScript Utility Types", badge: "TS", fileUrl: "/documents/interview/prep_ts_utility.md" }
      ]
    },
    {
      id: "phase15",
      title: "Phase 15: Testing & Quality",
      desc: "Unit Testing, E2E, Core Web Vitals, and a11y.",
      badge: "Phase 15",
      group: "Phase 15",
      icon: <QuizIcon />,
      problems: [
        { id: "z2h_p15_1", title: "Unit Testing Fundamentals", badge: "Unit", fileUrl: "/documents/interview/prep_test_unit.md" },
        { id: "z2h_p15_2", title: "React Testing Library", badge: "RTL", fileUrl: "/documents/interview/prep_test_integration.md" },
        { id: "z2h_p15_3", title: "Core Web Vitals", badge: "Perf", fileUrl: "/documents/frontend_hld/fe_arch_3.md" },
        { id: "z2h_p15_4", title: "Accessibility (a11y) Mastery", badge: "a11y", fileUrl: "/documents/interview/prep_a11y_basics.md" },
        { id: "z2h_p15_5", title: "E2E Testing with Playwright", badge: "E2E", fileUrl: "/documents/interview/prep_test_e2e.md" }
      ]
    },
    {
      id: "phase16",
      title: "Phase 16: System Design Fundamentals",
      desc: "Architecting massive frontends and communication protocols.",
      badge: "Phase 16",
      group: "Phase 16",
      icon: <DashboardIcon />,
      problems: [
        { id: "z2h_p16_1", title: "Component Driven Architecture", badge: "HLD", fileUrl: "/documents/frontend_hld/fe_arch_1.md" },
        { id: "z2h_p16_2", title: "Networking & Communication", badge: "Network", fileUrl: "/documents/frontend_hld/fe_arch_2.md" },
        { id: "z2h_p16_3", title: "Micro-Frontends & Federation", badge: "Scale", fileUrl: "/documents/frontend_hld/fe_arch_15.md" },
        { id: "z2h_p16_4", title: "Offline Storage Strategy", badge: "PWA", fileUrl: "/documents/frontend_hld/fe_arch_4.md" }
      ]
    },
    {
      id: "phase17",
      title: "Phase 17: Interview Case Studies",
      desc: "Real-world machine coding and architect interviews.",
      badge: "Phase 17",
      group: "Phase 17",
      icon: <SchemaIcon />,
      problems: [
        { id: "z2h_p17_1", title: "Design a News Feed", badge: "Case Study", fileUrl: "/documents/frontend_hld/fe_cs_3.md" },
        { id: "z2h_p17_2", title: "Design an Autocomplete", badge: "Case Study", fileUrl: "/documents/frontend_hld/fe_cs_1.md" },
        { id: "z2h_p17_3", title: "Implement a Custom Promise", badge: "Machine Coding", fileUrl: "/documents/interview/prep_machine_promise.md" },
        { id: "z2h_p17_4", title: "Design Google Docs (CRDTs)", badge: "Expert", fileUrl: "/documents/interview/prep_sys_collaborative.md" },
        { id: "z2h_p17_5", title: "Design a Multi-step Form", badge: "Machine Coding", fileUrl: "/documents/interview/prep_sys_form_wizard.md" }
      ]
    }
  ]
};
